// v0.6.12 (A2-209) — answer the establishment question the flag asks.
//
// CUSTOMER_ESTABLISHMENT_ASSUMED_FROM_VAT_NUMBER means the reverse
// charge is resting on the customer's VAT-number PREFIX, where that
// country's law actually asks whether the customer is ESTABLISHED
// there — ES, GR, IT, MT, NL, PL, SK. The flag's own action text told
// the operator to charge local VAT if the customer is established
// elsewhere, and nothing let them do it.
//
// ADVISORY, NOT BLOCKING. Leaving it unanswered routes exactly as
// before; this adds a way to answer, not a demand to.
//
// BULK BY CUSTOMER. Establishment is a fact about the CUSTOMER, not
// about a row, so answering it for one row and not its siblings is
// usually wrong. The group mode answers every selected row at once and
// the backend records one decision per row, so the audit trail still
// says what was decided about each.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveCustomerEstablishment } from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { ESTABLISHMENT_REASONS } from "../../lib/reviewReasonPresets";

// Kept in step with backend/engine/art194_election.py's
// ESTABLISHED_HERE / ESTABLISHED_ELSEWHERE. A jest test pins the values.
// v0.6.12 — NO arbitrary pixel text sizes here. index.css says plainly
// that arbitrary per-pixel values are how the ten sizes happened, and
// designTokens' budget targets ZERO of them; the per-pixel class names
// used elsewhere are a compatibility shim mapping onto the scale, not
// sizes to copy into new code. (This comment deliberately avoids
// writing one out: the budget counts occurrences in file TEXT, so even
// an explanation of the rule would spend against it.)
//
// `text-xs` is the scale's own 13px step with a real class name, so it
// costs nothing against the ratchet. It renders ~1.5px larger than the
// older resolvers beside it; that is the direction the design system is
// moving, and matching them would have meant adding to the very count
// the budget exists to drive down.
const SZ = "text-xs";
const SZ_TITLE = "text-xs font-bold";

export const ESTABLISHMENT_ANSWERS = [
  {
    value: "ESTABLISHED_HERE",
    // A2-540 (operator 2026-10-26): each answer says its VAT outcome up front.
    label: "Established there: the customer pays the VAT (reverse charge)",
    outcome: "Reverse charge stands — the customer accounts for the VAT.",
  },
  {
    value: "ESTABLISHED_ELSEWHERE",
    label: "Established elsewhere: you charge that country's VAT",
    outcome: "No reverse charge — you charge that country's VAT, which needs a registration there.",
  },
];

export const CustomerEstablishmentResolver = ({
  tids, calcId, calcFrozen, acked, buyerVat, onResolved, hideChip = false,
  saved = null,   // A2-540: the answer already on the row (ESTABLISHED_HERE / _ELSEWHERE)
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [changing, setChanging] = useState(false);   // A2-540: reopen a saved answer

  const ids = Array.isArray(tids) ? tids : [tids];
  const many = ids.length > 1;
  const key = many ? `grp-${ids.length}` : ids[0];

  const reset = () => { setAnswer(""); setReason(""); setPreset(""); setErr(null); };
  const cleaned = reason.trim();
  const valid = !!answer && cleaned.length >= 8 && !/[<>]/.test(cleaned);
  const picked = ESTABLISHMENT_ANSWERS.find((a) => a.value === answer);

  // A2-540 (operator 2026-10-26: "the banner remains after I make selections")
  // — a saved answer is shown as the answer, with the way to change it.
  const savedAnswer = ESTABLISHMENT_ANSWERS.find((a) => a.value === saved);
  if (savedAnswer && !changing) {
    return (
      <span data-testid={`est-saved-${key}`} className={`text-emerald-700 dark:text-emerald-300 ${SZ}`}>
        ✓ {savedAnswer.label}
        {!calcFrozen && (
          <button type="button" data-testid={`est-change-${key}`} onClick={() => { setChanging(true); setOpen(true); }}
            className="ml-2 underline text-slate-600 dark:text-slate-300">
            {t("ui.review_grouped.est_change", { defaultValue: "Change" })}
          </button>
        )}
      </span>
    );
  }
  if (acked && !changing) {
    return (
      <>
        {!hideChip && (
        <span
          data-testid={`rg-txn-${key}`}
          data-resolved="true"
          className={`px-2 py-1 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200 truncate tabular-nums ${SZ}`}
        >
          ✓ {many ? t("ui.review_grouped.n_rows", { n: ids.length, defaultValue: `${ids.length} rows` }) : `${ids[0].slice(0, 12)}…`}
        </span>
        )}
      </>
    );
  }

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await resolveCustomerEstablishment(calcId, {
        txn_ids: ids, answer, reason: cleaned,
      });
      setOpen(false); setChanging(false); reset();
      toast.success(
        t("ui.review_grouped.est_toast_ok", { defaultValue: "Saved — figures change at the next re-run" }),
        { description: `${many ? t("ui.review_grouped.n_rows", { n: ids.length, defaultValue: `${ids.length} rows` }) : `${ids[0].slice(0, 12)}…`} → ${picked ? picked.label : answer}` },
      );
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(
        t("ui.review_grouped.est_toast_fail", { defaultValue: "Could not save" }),
        { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {!hideChip && (
        <span
          data-testid={`rg-txn-${key}`}
          data-resolved="false"
          className={`px-2 py-1 rounded border bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 truncate tabular-nums ${SZ}`}
        >
          {many ? t("ui.review_grouped.n_rows", { n: ids.length, defaultValue: `${ids.length} rows` }) : `${ids[0].slice(0, 12)}…`}
        </span>
        )}
        {!calcFrozen && (
          <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={busy}
                data-testid={`est-resolve-open-${key}`}
                data-busy={busy ? "true" : undefined}
                className={`tabular-nums px-2 py-1 rounded border ${busy
                  ? "border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 cursor-wait"
                  : "border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-200"} ${SZ}`}
              >
                {busy
                  ? t("ui.review_grouped.est_submitting", { defaultValue: "Saving…" })
                  : many
                  ? t("ui.review_grouped.est_resolve_btn_bulk", {
                    defaultValue: "Answer for {{count}} rows →", count: ids.length,
                  })
                    : t("ui.review_grouped.est_resolve_btn", { defaultValue: "Where is the customer? →" })}
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid={`est-resolve-popover-${key}`}
              className="w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
            >
              <div className={`text-slate-700 dark:text-slate-200 ${SZ_TITLE}`}>
                {t("ui.review_grouped.est_resolve_title", { defaultValue: "Where is this customer established?" })}
              </div>
              <div className={`text-slate-500 dark:text-slate-400 leading-snug ${SZ}`}>
                {t("ui.review_grouped.est_resolve_help", {
                  defaultValue:
                    "The law here asks where the customer is established, not just where they hold a VAT number. Your answer decides who pays the VAT.",
                })}
              </div>
              {buyerVat && (
                <div
                  data-testid={`est-resolve-vat-${key}`}
                  className={`rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 tabular-nums ${SZ}`}
                >
                  {t("ui.review_grouped.est_resolve_vat", { defaultValue: "Customer VAT number" })}: {buyerVat}
                </div>
              )}
              {many && (
                <div
                  data-testid={`est-resolve-bulk-note-${key}`}
                  className={`rounded px-2 py-1 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 ${SZ}`}
                >
                  {t("ui.review_grouped.est_resolve_bulk_note", {
                    defaultValue:
                      "Answers all {{count}} selected rows. Each keeps its own audit record.",
                    count: ids.length,
                  })}
                </div>
              )}
              <select
                data-testid={`est-resolve-answer-${key}`}
                value={answer}
                onChange={(e) => {
                  // v0.6.12 A5-27 — each answer has its own reasons; a
                  // reason picked for the other answer must not survive.
                  setAnswer(e.target.value); setPreset(""); setReason("");
                }}
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs"
              >
                <option value="">
                  {t("ui.review_grouped.est_answer_placeholder", { defaultValue: "Select…" })}
                </option>
                {ESTABLISHMENT_ANSWERS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              {picked && (
                <div
                  data-testid={`est-resolve-outcome-${key}`}
                  className={`rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 ${SZ}`}
                >
                  {picked.outcome}
                </div>
              )}
              {answer && (
                <PresetReasonSelect
                  testid={`est-resolve-reason-preset-${key}`}
                  presets={ESTABLISHMENT_REASONS[answer] || []}
                  preset={preset}
                  onPick={(p, v) => { setPreset(p); setReason(v); }}
                />
              )}
              {err && (
                <div data-testid={`est-resolve-error-${key}`} className={`text-red-700 dark:text-red-300 ${SZ}`}>
                  {err}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`est-resolve-cancel-${key}`}
                  onClick={() => { setOpen(false); setChanging(false); reset(); }}
                  className={`underline text-slate-600 dark:text-slate-300 ${SZ}`}
                >
                  {t("ui.review_grouped.est_cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  data-testid={`est-resolve-submit-${key}`}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/resolve-customer-establishment"
                  disabled={busy || !valid}
                  onClick={submit}
                  className={`font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white ${SZ}`}
                >
                  {busy
                    ? t("ui.review_grouped.est_submitting", { defaultValue: "Saving…" })
                    : t("ui.review_grouped.est_submit", { defaultValue: "Save" })}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
};

export default CustomerEstablishmentResolver;
